package com.sweetlime.dungeonamigo.repository

import com.sweetlime.dungeonamigo.model.DmEntity
import com.sweetlime.dungeonamigo.model.RollEvent
import com.sweetlime.dungeonamigo.model.SessionParticipant
import com.sweetlime.dungeonamigo.model.SessionRoom
import com.sweetlime.dungeonamigo.util.currentTimeMillis
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlin.random.Random

class FirebaseSessionRepository(
    private val firestore: FirebaseFirestore,
) : SessionRepository {

    private fun roomsCollection() = firestore.collection("sessionRooms")
    private fun codesCollection() = firestore.collection("sessionCodes")
    private fun participantsCollection(roomId: String) =
        roomsCollection().document(roomId).collection("participants")
    private fun rollFeedCollection(roomId: String) =
        roomsCollection().document(roomId).collection("rollFeed")
    private fun dmEntitiesCollection(roomId: String) =
        roomsCollection().document(roomId).collection("dmEntities")

    private fun generateRoomCode(): String {
        val chars = "ABCDEFGHJKLMNPQRSTUVWXYZ"  // no I or O — too easy to confuse
        return (1..4).map { chars[Random.nextInt(chars.length)] }.joinToString("")
    }

    private suspend fun generateUniqueRoomCode(): String {
        repeat(10) {
            val code = generateRoomCode()
            if (getRoomIdByCode(code) == null) return code
        }
        return generateRoomCode()
    }

    override suspend fun createRoom(
        dmUserId: String,
        dmDisplayName: String,
        campaignName: String,
    ): SessionRoom {
        val roomRef = roomsCollection().document
        val roomId = roomRef.id
        val roomCode = generateUniqueRoomCode()
        val room = SessionRoom(
            id = roomId,
            roomCode = roomCode,
            dmUserId = dmUserId,
            dmDisplayName = dmDisplayName,
            campaignName = campaignName,
            isActive = true,
            createdAt = currentTimeMillis(),
        )
        // DM participant written atomically with room + code index.
        // isDm=true so UI can distinguish DM from players without a separate room lookup.
        val dmParticipant = SessionParticipant(
            userId = dmUserId,
            displayName = dmDisplayName,
            isDm = true,
            isConnected = true,
        )
        val batch = firestore.batch()
        batch.set(roomRef, room)
        batch.set(codesCollection().document(roomCode), mapOf("roomId" to roomId))
        batch.set(participantsCollection(roomId).document(dmUserId), dmParticipant)
        batch.commit()
        return room
    }

    override suspend fun endSession(roomId: String) {
        val roomDoc = runCatching {
            roomsCollection().document(roomId).get().data<SessionRoom>()
        }.getOrNull() ?: return

        val batch = firestore.batch()
        batch.update(roomsCollection().document(roomId), "isActive" to false)
        batch.delete(codesCollection().document(roomDoc.roomCode))
        batch.commit()
    }

    override suspend fun joinRoom(roomCode: String, participant: SessionParticipant): String {
        val roomId = getRoomIdByCode(roomCode)
            ?: throw Exception("Room not found: $roomCode")
        participantsCollection(roomId).document(participant.userId).set(participant)
        return roomId
    }

    override suspend fun leaveRoom(roomId: String, userId: String) {
        runCatching {
            participantsCollection(roomId).document(userId).update("isConnected" to false)
        }
    }

    override fun observeRoom(roomId: String): Flow<SessionRoom?> =
        roomsCollection().document(roomId).snapshots.map { snap ->
            if (snap.exists) runCatching { snap.data<SessionRoom>() }.getOrNull() else null
        }

    override fun observeParticipants(roomId: String): Flow<List<SessionParticipant>> =
        participantsCollection(roomId).snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                runCatching { doc.data<SessionParticipant>() }.getOrNull()
            }
        }

    override fun observeRollFeed(roomId: String): Flow<List<RollEvent>> =
        rollFeedCollection(roomId).snapshots.map { snapshot ->
            snapshot.documents
                .mapNotNull { doc -> runCatching { doc.data<RollEvent>() }.getOrNull() }
                .sortedByDescending { it.timestamp }
                .take(20)
        }

    override suspend fun updateHp(roomId: String, userId: String, currentHp: Int, temporaryHp: Int) {
        participantsCollection(roomId).document(userId).update(
            "currentHp" to currentHp,
            "temporaryHp" to temporaryHp,
        )
    }

    override suspend fun updateInitiative(
        roomId: String,
        userId: String,
        roll: Int,
        initiativeValue: Int,
    ) {
        participantsCollection(roomId).document(userId).update(
            "initiativeRoll" to roll,
            "initiative" to initiativeValue,
        )
    }

    override suspend fun updateConditions(roomId: String, userId: String, conditions: List<String>) {
        participantsCollection(roomId).document(userId).update("conditions" to conditions)
    }

    override suspend fun postRoll(roomId: String, roll: RollEvent) {
        val col = rollFeedCollection(roomId)
        val ref = col.document
        ref.set(roll.copy(id = ref.id))
        // Trim feed to 20 entries
        val all = runCatching { col.get() }.getOrNull() ?: return
        if (all.documents.size > 20) {
            val oldest = all.documents
                .mapNotNull { doc -> runCatching { doc.data<RollEvent>() }.getOrNull() }
                .sortedBy { it.timestamp }
                .take(all.documents.size - 20)
            oldest.forEach { old -> col.document(old.id).delete() }
        }
    }

    override suspend fun setParticipantInitiative(roomId: String, userId: String, initiative: Int) {
        participantsCollection(roomId).document(userId).update("initiativeRoll" to initiative)
    }

    override suspend fun removeParticipant(roomId: String, userId: String) {
        participantsCollection(roomId).document(userId).delete()
    }

    override suspend fun getRoomIdByCode(code: String): String? = runCatching {
        val snap = codesCollection().document(code).get()
        if (snap.exists) snap.data<Map<String, String>>()["roomId"] else null
    }.getOrNull()

    override fun observeDmEntities(roomId: String): Flow<List<DmEntity>> =
        dmEntitiesCollection(roomId).snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                runCatching { doc.data<DmEntity>() }.getOrElse { e ->
                    println("[DmEntity] deserialize failed for ${doc.id}: ${e::class.simpleName}: ${e.message}")
                    null
                }
            }
        }

    override suspend fun addDmEntity(entity: DmEntity) {
        dmEntitiesCollection(entity.roomId).document(entity.id).set(entity)
    }

    override suspend fun updateDmEntityHp(
        roomId: String,
        entityId: String,
        currentHp: Int,
        temporaryHp: Int,
    ) {
        dmEntitiesCollection(roomId).document(entityId).update(
            "currentHp" to currentHp,
            "temporaryHp" to temporaryHp,
        )
    }

    override suspend fun updateDmEntityInitiative(
        roomId: String,
        entityId: String,
        initiativeRoll: Int,
        initiative: Int,
    ) {
        dmEntitiesCollection(roomId).document(entityId).update(
            "initiativeRoll" to initiativeRoll,
            "initiative" to initiative,
        )
    }

    override suspend fun updateDmEntityConditions(
        roomId: String,
        entityId: String,
        conditions: List<String>,
    ) {
        dmEntitiesCollection(roomId).document(entityId).update("conditions" to conditions)
    }

    override suspend fun toggleDmEntityDefeated(
        roomId: String,
        entityId: String,
        isDefeated: Boolean,
    ) {
        dmEntitiesCollection(roomId).document(entityId).update("isDefeated" to isDefeated)
    }

    override suspend fun removeDmEntity(roomId: String, entityId: String) {
        dmEntitiesCollection(roomId).document(entityId).delete()
    }
}
